import { useCrmLocale } from '../ui/locale/crmLocale';
import React from 'react';
import {
  createBrowserRouter,
  useLocation,
  useRouteError,
} from 'react-router-dom';

import {
  recordNavigationTelemetry,
  resolveRoutePolicy,
  type RouterBasename,
} from '../navigation';

const InternalServerError = React.lazy(() => import('../pages/errors/InternalServerError'));

// Router creation is intentionally colocated with its private route error element.
// eslint-disable-next-line react-refresh/only-export-components
function AppRouteError({ basename }: { basename: RouterBasename }) {
  const { t: crmText } = useCrmLocale();

  const error = useRouteError();
  const location = useLocation();
  const reported = React.useRef<{ error: unknown; pathname: string } | null>(null);

  React.useEffect(() => {
    const previous = reported.current;
    if (previous !== null
      && previous.error === error
      && previous.pathname === location.pathname) return;
    reported.current = { error, pathname: location.pathname };
    const policy = resolveRoutePolicy({ basename, pathname: location.pathname });
    recordNavigationTelemetry({
      name: 'reconstruction_failed',
      ...(policy ? { routeId: policy.id, shell: policy.shell } : {}),
      reason: 'render-error',
    });
  }, [basename, error, location.pathname]);

  return (
    <React.Suspense fallback={<div className="p-10 text-center text-slate-500">{crmText("Loading…")}</div>}>
      <InternalServerError />
    </React.Suspense>
  );
}

export function createAppRouter(
  element: React.ReactElement,
  basename: RouterBasename,
) {
  return createBrowserRouter([
    {
      path: '*',
      element,
      errorElement: <AppRouteError basename={basename} />,
    },
  ], { basename });
}
